import { AuthProvider } from 'react-admin';
import { i18nProvider } from './i18nProvider';
import { setSentryUser, clearSentryUser } from './sentry';
import {
  resourcePermissionMap,
  actionPermissionMap,
  specialPermissions,
  defaultRoutes,
} from './permissions';
import { isAdmin } from './utils';
import { API_URL } from './httpClient';

interface UserRole {
  id: number;
  name: string;
  systemName?: string | null;
}

interface CurrentUser {
  id: number;
  email: string;
  name?: string | null;
  roles: UserRole[];
  permissions: string[];
}

// Fetch current user info from backend
async function fetchCurrentUser(): Promise<CurrentUser | null> {
  const token = localStorage.getItem('token');
  const tenantUuid = localStorage.getItem('tenantUuid');

  if (!token || !tenantUuid) return null;

  try {
    const response = await fetch(`${API_URL}/auth/me`, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
        'x-tenant-uuid': tenantUuid,
        'x-tenant-user-auth': token,
      },
    });

    if (!response.ok) return null;

    const { data } = await response.json();
    return data as CurrentUser;
  } catch {
    return null;
  }
}

// Store user info in localStorage
function storeUserInfo(user: CurrentUser) {
  localStorage.setItem('userId', String(user.id));
  localStorage.setItem('userEmail', user.email);
  localStorage.setItem('userName', user.name || '');
  localStorage.setItem('userRoles', JSON.stringify(user.roles));
  localStorage.setItem('userPermissions', JSON.stringify(user.permissions));
}

// Clear user info from localStorage
function clearUserInfo() {
  localStorage.removeItem('userId');
  localStorage.removeItem('userEmail');
  localStorage.removeItem('userName');
  localStorage.removeItem('userRoles');
  localStorage.removeItem('userPermissions');
}

// Get stored permissions
function getStoredPermissions(): string[] {
  try {
    const permissions = localStorage.getItem('userPermissions');
    return permissions ? JSON.parse(permissions) : [];
  } catch {
    return [];
  }
}

// Check if user has a specific permission
function hasPermission(permissionCode: string): boolean {
  // Admin has all permissions
  if (isAdmin()) return true;

  const permissions = getStoredPermissions();
  return permissions.includes(permissionCode);
}

// Determine the default route based on user permissions
function getDefaultRedirect(): string | undefined {
  if (isAdmin()) return undefined; // Admin uses default (first resource)

  for (const [route, permission] of defaultRoutes) {
    if (hasPermission(permission)) return route;
  }

  return '/forbidden';
}

// Map React Admin resource/action to permission code
function mapToPermissionCode(
  resource: string,
  action: string
): string | null {
  // Check for special (non-standard) permission first
  if (specialPermissions[resource]) {
    return specialPermissions[resource];
  }

  const mappedResource = resourcePermissionMap[resource];
  const mappedAction = actionPermissionMap[action];

  if (!mappedResource || !mappedAction) return null;

  return `${mappedResource}:${mappedAction}`;
}

export const authProvider: AuthProvider = {
  login: async ({ email, password }) => {
    const tenantUuid = localStorage.getItem('tenantUuid');
    if (!tenantUuid) throw new Error('Tenant not found');

    // Step 1: Sign in
    const response = await fetch(`${API_URL}/auth/signIn`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'x-tenant-uuid': tenantUuid,
      },
      body: JSON.stringify({ email, password }),
    });

    if (!response.ok) {
      throw new Error(i18nProvider.translate('auth.invalidCreds'));
    }

    const { data } = await response.json();
    localStorage.setItem('token', data.accessToken);

    // Step 2: Fetch user info with permissions
    const user = await fetchCurrentUser();
    if (user) {
      storeUserInfo(user);
      // Set Sentry user context
      setSentryUser({ email: user.email, username: user.name || user.email });
    }

    // Redirect to the first resource the user can access
    const redirectTo = getDefaultRedirect();
    return redirectTo ? { redirectTo } : undefined;
  },

  logout: () => {
    localStorage.removeItem('token');
    clearUserInfo();
    clearSentryUser();
    return Promise.resolve();
  },

  checkError: ({ status }) => {
    if (status === 401) {
      localStorage.removeItem('token');
      clearUserInfo();
      return Promise.reject();
    }
    return Promise.resolve();
  },

  checkAuth: async () => {
    const token = localStorage.getItem('token');
    if (!token) return Promise.reject();

    // Always refresh user info on page load to get latest roles/permissions
    const user = await fetchCurrentUser();
    if (user) {
      storeUserInfo(user);
      setSentryUser({ email: user.email, username: user.name || user.email });
    } else {
      // Token is invalid
      localStorage.removeItem('token');
      clearUserInfo();
      clearSentryUser();
      return Promise.reject();
    }

    return Promise.resolve();
  },

  getPermissions: () => {
    const permissions = getStoredPermissions();
    return Promise.resolve(permissions);
  },

  getIdentity: () => {
    const id = localStorage.getItem('userId');
    const email = localStorage.getItem('userEmail');
    const name = localStorage.getItem('userName');

    if (!id) return Promise.resolve({ id: '', fullName: '' });

    return Promise.resolve({
      id,
      fullName: name || email || '',
      avatar: undefined,
    });
  },

  canAccess: async ({ action, resource }) => {
    // Admin has access to everything
    if (isAdmin()) {
      return true;
    }

    // Map to permission code and check
    const permissionCode = mapToPermissionCode(resource, action);

    if (!permissionCode) {
      // Unknown resource/action - allow by default or deny based on policy
      // For now, allow access to resources not in the map
      return true;
    }

    return hasPermission(permissionCode);
  },
};
